import { CircleEllipsis } from 'lucide-react'
import { useEffect, useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'

import { Button } from 'slab'
import { type QuotingEmployeeCensus, type QuotingEmployeeCensusFormat } from 'utils/web_api.schemas'

import {
  BeingProcessedBanner,
  InfoBanner,
  ProblemCreatingCensus,
} from './CensusBanners'
import { CensusDropdown } from './CensusDropdown'
import { CensusDetailsBox } from './ProspectCensusDetails'
import { type GrapeAPIErrorType } from '../hooks/GrapeAPIError'
import { useCreateCensus } from '../hooks/useCreateCensus'
import { useModifyQuote } from '../hooks/useModifyQuote'
import { Banner } from '../reusable/Banner'
import { FormSelectField } from '../reusable/form/FormFields'

type CensusFormProps = {
  file: FileList;
  format: QuotingEmployeeCensusFormat;
  file_name: string;
  quoting_prospect_employee_census_id: string;
}

// Form component to allow users to either upload a file to create a new census
// Or choose an existing census to be used for the quote
// This component can also be used for showing the file details
// for quote details page and census details page with read-only mode
export function CensusUploadForm ({
  census,
  prospectId,
  quoteId,
  onSuccess,
  onFailure,
  readOnly = false,
}: {
  census?: QuotingEmployeeCensus;
  prospectId: string;
  quoteId: string;
  onSuccess: () => void;
  onFailure: (error: GrapeAPIErrorType) => void;
  readOnly: boolean;
}) {
  const [submitting, setSubmitting] = useState(false)

  const createCensusMutation = useCreateCensus({ prospectId })
  const modifyQuoteMutation = useModifyQuote({ prospectId, quoteId })

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<CensusFormProps>({
    defaultValues: {
      format: census ? census.format : 'generic',
      quoting_prospect_employee_census_id: census ? census.id : '',
    },
  })

  const watchFile = watch('file')
  const watchCensusId = watch('quoting_prospect_employee_census_id')

  // Determine if form has a valid selection (file or census)
  const hasValidSelection = (watchFile && watchFile[0]) || (watchCensusId && watchCensusId.trim() !== '')

  // Clear root error when user uploads a file or selects a census
  useEffect(() => {
    if (hasValidSelection) {
      clearErrors('root')
    }
  }, [hasValidSelection, clearErrors])

  const onSubmit: SubmitHandler<CensusFormProps> = data => {
    // Validate that either a file is uploaded or an existing census is selected
    const hasFile = data.file && data.file[0]
    const hasCensusId = data.quoting_prospect_employee_census_id && data.quoting_prospect_employee_census_id.trim() !== ''

    if (!hasFile && !hasCensusId) {
      setError('root', {
        type: 'manual',
        message: 'Please upload a census file or select an existing census.',
      })
      return
    }

    clearErrors('root')
    setSubmitting(true)
    const formData = new FormData()
    if (hasFile) {
      formData.append('format', data.format)
      formData.append('file', data.file[0])
      formData.append('file_name', data.file[0].name)

      // If we are uploading a new file, then we are creating a new census
      // based on the new file and attach it to the quote upon success
      createCensusMutation.mutate(formData, {
        onSuccess: (census) => {
          modifyQuoteMutation.mutate(
            { censusId: census.id },
            {
              onSuccess: () => {
                setSubmitting(false)
                onSuccess()
              },
              onError: (error) => {
                setSubmitting(false)
                onFailure(error)
              },
            }
          )
        },
        onError: (error) => {
          setSubmitting(false)
          onFailure(error)
        },
      })
    } else {
      // If we are choosing an existing census, then we attach it to the quote right away
      modifyQuoteMutation.mutate(
        { censusId: data.quoting_prospect_employee_census_id },
        {
          onSuccess: () => {
            setSubmitting(false)
            onSuccess()
          },
          onError: (error) => {
            setSubmitting(false)
            onFailure(error)
          },
        }
      )
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className='pt-6 pb-10'>
        <div className='pb-3'>
          <FormSelectField<CensusFormProps>
            disabled={submitting || readOnly}
            error={errors.format}
            fieldId='format'
            fieldLabel='Payroll provider'
            fieldOptions={[
              { label: 'Default', value: 'generic' },
              { label: 'Rippling', value: 'rippling' },
            ]}
            register={register}
            validationRule={{
              required: 'File format is required.',
            }}
          />
        </div>
        <label
          className='label-default font-medium text-ds-text-secondary mb-2'
          htmlFor='file'
        >
          Upload new file
        </label>
        {
          // TODO: fix the bug where census?.filename is showing stale data
          // for the reupload case. This might be fixed if I make the file upload
          // to be a standalone component
          readOnly && census
            ? (
              <div className='rounded-lg border border-ds-border-primary-default bg-white p-4 flex gap-x-2 items-center'>
                <CircleEllipsis size={24} className='text-ds-icon-caution' />
                <div className='text-xs text-ds-text-default'>
                  {census?.file_name}
                </div>
              </div>
              )
            : (
              <input
                id='file'
                type='file'
                className='input-default'
                {...register('file')}
                accept='text/csv'
                disabled={submitting || readOnly}
                multiple={false}
              />
              )
        }
        {!readOnly && (
          <CensusDropdown<CensusFormProps>
            disabled={submitting || readOnly}
            error={errors.quoting_prospect_employee_census_id}
            prospectId={prospectId}
            register={register}
          />
        )}
        {errors.root && (
          <div className='mt-4'>
            <Banner type='critical' titleText='Census required'>
              <p>{errors.root.message}</p>
            </Banner>
          </div>
        )}
      </div>
      {!readOnly && (
        <Button
          variant='primary'
          size='large'
          disabled={submitting || !hasValidSelection}
          aria-disabled={submitting || !hasValidSelection}
          type='submit'
        >
          Next
        </Button>
      )}
    </form>
  )
}

// This is going to be the entry point for the quote creation: census upload
// It can also be reused for the quote detail page where
// 1) quote creation is still in progress
// 2) census upload is in progress, and the latest upload has failed
// In that case, this screen will be used to prompt the users to do the reupload
// (The prop census is used to show the details of the failed upload)
export function QuoteCreateCensusUpload ({
  census,
  prospectId,
  quoteId,
}: {
  census?: QuotingEmployeeCensus;
  prospectId: string;
  quoteId: string;
}) {
  const [stage, setStage] = useState('upload')
  const [errors, setErrors] = useState<GrapeAPIErrorType>()
  const onSuccess = () => {
    setStage('success')
  }
  const onFailure = (error: GrapeAPIErrorType) => {
    setStage('error')
    setErrors(error)
  }

  return (
    <div>
      <h2 className='h2 font-normal'>Upload census file</h2>
      <div className='flex gap-x-10'>
        <div className='flex-1'>
          <CensusUploadForm
            census={census}
            prospectId={prospectId}
            quoteId={quoteId}
            onSuccess={onSuccess}
            onFailure={onFailure}
            readOnly={stage === 'success'}
          />
        </div>
        <div className='flex-1'>
          {stage === 'upload' && census?.status === 'failed' && (
            <>
              <h3 className='h3 mb-6'>Details of previous upload attempt</h3>
              <CensusDetailsBox census={census} />
            </>
          )}
          {stage === 'upload' && !census && <InfoBanner />}
          {stage === 'success' && <BeingProcessedBanner />}
          {stage === 'error' && errors && (
            <ProblemCreatingCensus errors={errors} />
          )}
        </div>
      </div>
    </div>
  )
}
